import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import type { MailingFormInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-form.dto';
import { MailingTypeEnum } from '@zaoblako/shared/modules/notification/enums/mailing.type.enum';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { DashboardService } from '@zaoblako/web/core/services/dashboard/dashboard.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { Integration1cService } from '@zaoblako/web/core/services/integration/integration1c.service';
import { TenantService } from '@zaoblako/web/core/services/security/tenant.service';
import { NewsletterWelcomeSystemChangePlanningComponent } from '@zaoblako/web/shared/components/newsletter-welcome-system/components/newsletter-welcome-system-change-planning/newsletter-welcome-system-change-planning.component';
import { NewsletterWelcomeSystemChangeTextComponent } from '@zaoblako/web/shared/components/newsletter-welcome-system/components/newsletter-welcome-system-change-text/newsletter-welcome-system-change-text.component';
import { NewsletterWelcomeSystemConnect1cComponent } from '@zaoblako/web/shared/components/newsletter-welcome-system/components/newsletter-welcome-system-connect-1c/newsletter-welcome-system-connect-1c.component';
import { NewsletterWelcomeSystemCreateCompanyComponent } from '@zaoblako/web/shared/components/newsletter-welcome-system/components/newsletter-welcome-system-create-company/newsletter-welcome-system-create-company.component';
import { NewsletterWelcomeSystemCreateContactsComponent } from '@zaoblako/web/shared/components/newsletter-welcome-system/components/newsletter-welcome-system-create-contacts/newsletter-welcome-system-create-contacts.component';
import { NewsletterWelcomeSystemExcelComponent } from '@zaoblako/web/shared/components/newsletter-welcome-system/components/newsletter-welcome-system-excel/newsletter-welcome-system-excel.component';
import { NewsletterWelcomeSystemSelectChannelComponent } from '@zaoblako/web/shared/components/newsletter-welcome-system/components/newsletter-welcome-system-select-channel/newsletter-welcome-system-select-channel.component';
import { NewsletterWelcomeSystemStepEnum } from '@zaoblako/web/shared/components/newsletter-welcome-system/enums/newsletter-welcome-system-step.enum';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'crm-newsletter-welcome-system',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    NewsletterWelcomeSystemConnect1cComponent,
    NewsletterWelcomeSystemChangeTextComponent,
    NewsletterWelcomeSystemExcelComponent,
    NewsletterWelcomeSystemSelectChannelComponent,
    NewsletterWelcomeSystemChangePlanningComponent,
    NewsletterWelcomeSystemCreateCompanyComponent,
    NewsletterWelcomeSystemCreateContactsComponent
  ],
  providers: [DashboardService],
  templateUrl: './newsletter-welcome-system.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsletterWelcomeSystemComponent implements OnInit, OnDestroy {
  public step: NewsletterWelcomeSystemStepEnum = NewsletterWelcomeSystemStepEnum.SELECT_TYPE;
  public newsletter = <MailingFormInterface>{};
  public typeNewsletter: '1c' | 'excel';

  public companies: CompanyFindInterface[] = [];
  public databases: Connection1cFindInterface[] = [];
  public contragents: ContragentFindInterface[] = [];

  public get NewsletterWelcomeSystemStepEnum() {
    return NewsletterWelcomeSystemStepEnum;
  }

  public constructor(
    private readonly companyService: CompanyService,
    private readonly integration1cService: Integration1cService,
    private readonly dashboardService: DashboardService,
    private readonly tenantService: TenantService,
    private readonly globalService: GlobalService,
    private readonly ref: DynamicDialogRef,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    forkJoin([
      this.companyService.findMany(),
      this.integration1cService.find<Connection1cFindInterface>(),
      this.dashboardService.getDebtors({
        date: moment().format('YYYY-MM-DD') as unknown as Date,
        onlyPositive: true
      })
    ]).subscribe(([{ items: companies }, databases, dataDebtor]) => {
      this.companies = companies;
      this.databases = databases;
      this.contragents = dataDebtor.items?.map((item) => item.contragent).filter((contragent) => contragent?.id);
      this.cdRef.markForCheck();
    });
  }

  public ngOnDestroy() {
    this.ref?.close();
  }

  public selectType(step: NewsletterWelcomeSystemStepEnum) {
    if (step === NewsletterWelcomeSystemStepEnum.CONNECT_TO_1C) {
      this.typeNewsletter = '1c';
      this.newsletter.type = MailingTypeEnum.DEBTORS;
    } else {
      this.typeNewsletter = 'excel';
      this.newsletter.type = MailingTypeEnum.DEBTORS_FROM_FILE;
    }
    this.step = step;
  }

  public clickBack() {
    switch (this.step) {
      case NewsletterWelcomeSystemStepEnum.CONNECT_TO_1C: {
        this.step = NewsletterWelcomeSystemStepEnum.SELECT_TYPE;
        break;
      }
      case NewsletterWelcomeSystemStepEnum.CREATE_COMPANY: {
        this.step = NewsletterWelcomeSystemStepEnum.CONNECT_TO_1C;
        break;
      }
      case NewsletterWelcomeSystemStepEnum.EXCEL_TEMPLATE: {
        this.step = NewsletterWelcomeSystemStepEnum.SELECT_TYPE;
        break;
      }
      case NewsletterWelcomeSystemStepEnum.NEWSLETTER_TEXT: {
        if (this.typeNewsletter === '1c') {
          if (this.companies?.length) {
            this.step = NewsletterWelcomeSystemStepEnum.CONNECT_TO_1C;
          } else {
            this.step = NewsletterWelcomeSystemStepEnum.CREATE_COMPANY;
          }
        } else {
          this.step = NewsletterWelcomeSystemStepEnum.EXCEL_TEMPLATE;
        }
        break;
      }
      case NewsletterWelcomeSystemStepEnum.NEWSLETTER_CHANNEL: {
        this.step = NewsletterWelcomeSystemStepEnum.NEWSLETTER_TEXT;
        break;
      }
      case NewsletterWelcomeSystemStepEnum.CREATE_CONTACTS: {
        this.step = NewsletterWelcomeSystemStepEnum.NEWSLETTER_CHANNEL;
        break;
      }
      case NewsletterWelcomeSystemStepEnum.NEWSLETTER_PLANNING: {
        if (
          this.newsletter.type === MailingTypeEnum.DEBTORS &&
          !this.contragents?.every((contragent) => contragent.externalUsers?.length)
        ) {
          this.step = NewsletterWelcomeSystemStepEnum.CREATE_CONTACTS;
        } else {
          this.step = NewsletterWelcomeSystemStepEnum.NEWSLETTER_CHANNEL;
        }
        break;
      }
    }
  }

  public closeModal(isCreateNewsletter = false) {
    this.ref?.close();
    if (isCreateNewsletter) {
      location.reload();
    } else {
      this.tenantService.disableDebitWelcome({ enableWelcomeLater: true }).subscribe();
      this.globalService.setTimerShowWelcomeSystem(30000);
    }
  }

  public addCompany(company: CompanyFindInterface) {
    this.companies.push(company);
    this.newsletter.companies = [company];
    this.step = NewsletterWelcomeSystemStepEnum.NEWSLETTER_TEXT;
  }

  public nextStepFrom1C() {
    if (this.companies?.length) {
      this.step = NewsletterWelcomeSystemStepEnum.NEWSLETTER_TEXT;
      this.newsletter.companies = this.companies;
    } else {
      this.step = NewsletterWelcomeSystemStepEnum.CREATE_COMPANY;
    }
  }

  public nextStepFromSelectChannel() {
    if (
      this.newsletter.type === MailingTypeEnum.DEBTORS &&
      !this.contragents?.every((contragent) => contragent.externalUsers?.length)
    ) {
      this.step = NewsletterWelcomeSystemStepEnum.CREATE_CONTACTS;
    } else {
      this.step = NewsletterWelcomeSystemStepEnum.NEWSLETTER_PLANNING;
    }
  }

  public nextStepFromCreateContacts(contragents: ContragentFindInterface[]) {
    this.contragents = contragents;
    this.step = NewsletterWelcomeSystemStepEnum.NEWSLETTER_PLANNING;
  }
}
